import type { MedplumClient } from '@medplum/core';
import type { Coding, DocumentReference, Reference } from '@medplum/fhirtypes';
import {
  CLIENT_VISIBLE_TAG_CODE,
  CLIENT_VISIBLE_TAG_SYSTEM,
} from '../pages/client-portal/utils/clientPortalClassifiers';

/** Minimal valid PDF (single blank page) for seeded demo attachments. */
export const SEED_DEMO_PDF_BASE64 =
  'JVBERi0xLjcKCjEgMCBvYmogIDw8IC9UeXBlIC9DYXRhbG9nCiAgICAvUGFnZXMgMiAwIFI+PgplbmRvYmoKCjIgMCBvYmogPDwvVHlwZS9QYWdlcy9LaWRzWzMgMCBSXS9Db3VudCAxPj4KZW5kb2JqCgozIDAgb2JqIDw8Ci9UeXBlIC9QYWdlCi9QYXJlbnQgMiAwIFIKL01lZGlhQm94IFswIDAgNjEyIDc5Ml0KPj4KZW5kb2JqCnhyZWYKMCA0CjAwMDAwMDAwMDAgNjU1MzUgZiAKMDAwMDAwMDAxNSAwMDAwMCBuIAowMDAwMDAwMDY0IDAwMDAwIG4gCjAwMDAwMDAxMjEgMDAwMDAgbiAKdHJhaWxlcgo8PCAvU2l6ZSA0IC9Sb290IDEgMCBSPj4Kc3RhcnR4cmVmCjE5MQolJUVPRgo=';

/** 1×1 JPEG for seeded image demo attachments. */
export const SEED_DEMO_JPEG_BASE64 =
  '/9j/4AAQSkZJRgABAQEAYABgAAD/2wBDAAgGBgcGBQgHBwcJCQgKDBQNDAsLDBkSEw8UHRofHh0aHBwgJC4nICIsIxwcKDcpLDAxNDQ0Hyc5PTgyPC4zNDL/2wBDAQkJCQwLDBgNDRgyIRwhMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjIyMjL/wAARCAABAAEDASIAAhEBAxEB/8QAFQABAQAAAAAAAAAAAAAAAAAAAAX/xAAUEAEAAAAAAAAAAAAAAAAAAAAA/8QAFQEBAQAAAAAAAAAAAAAAAAAAAAX/xAAUEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwCwAA8A/9k=';

export function documentReferenceMetaForPatient(params: {
  patientId: string;
  accountRefs: Reference[];
  tag?: NonNullable<DocumentReference['meta']>['tag'];
}): NonNullable<DocumentReference['meta']> {
  return {
    account: { reference: `Patient/${params.patientId}` },
    ...(params.accountRefs.length > 0 ? { accounts: params.accountRefs } : {}),
    ...(params.tag ? { tag: params.tag } : {}),
  };
}

function withoutClientVisibleTag(
  tags: NonNullable<DocumentReference['meta']>['tag'] | undefined,
): Coding[] {
  return (tags ?? []).filter(
    (t) => !(t.system === CLIENT_VISIBLE_TAG_SYSTEM && t.code === CLIENT_VISIBLE_TAG_CODE),
  );
}

/**
 * Share or unshare a chart file with the client portal by toggling the
 * client-visible meta tag and stamping Patient compartment metadata so
 * providers can update and clients receive WS/search events.
 */
export async function setDocumentSharedWithClient(
  medplum: MedplumClient,
  params: {
    documentReferenceId: string;
    patientId: string;
    accountRefs: Reference[];
    shared: boolean;
  },
): Promise<DocumentReference> {
  const dr = await medplum.readResource('DocumentReference', params.documentReferenceId);
  const baseTags = withoutClientVisibleTag(dr.meta?.tag);
  const tag = params.shared
    ? [...baseTags, { system: CLIENT_VISIBLE_TAG_SYSTEM, code: CLIENT_VISIBLE_TAG_CODE }]
    : baseTags;

  return medplum.updateResource({
    ...dr,
    meta: {
      ...(dr.meta ?? {}),
      ...documentReferenceMetaForPatient({
        patientId: params.patientId,
        accountRefs: params.accountRefs,
        tag,
      }),
    },
  });
}
